import axios from "axios";
import {
  getAuthToken,
  setAuthToken,
  setUserData,
  clearAuthData,
} from "../utils/index";

/**
 * HTTP Status Codes
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
};

/**
 * Handle API errors
 */
const handleApiError = (error) => {
  if (error.response) {
    const { status, data } = error.response;

    console.error("API Error Response:", data);

    // Handle 401 Unauthorized
    if (status === HTTP_STATUS.UNAUTHORIZED) {
      // Don't auto-logout for blob/document requests — they may 401 due to
      // file path issues, not expired tokens. Check if it's a document request.
      const isDocumentRequest =
        error.config?.responseType === "blob" ||
        error.config?.url?.includes("view-document") ||
        error.config?.url?.includes("download");

      if (isDocumentRequest) {
        console.error(
          "[apiClient] 401 on document request — NOT logging out. URL:",
          error.config?.url,
        );
        console.error(
          "[apiClient] filePath param:",
          error.config?.params?.filePath,
        );
        throw new Error(
          "Document access denied (401). The file path may be invalid or the document does not exist.",
        );
      }

      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/";
      throw new Error("Session expired. Please login again.");
    }

    // Handle other status codes
    switch (status) {
      case HTTP_STATUS.NOT_FOUND:
        throw new Error(data?.message || "Resource not found.");

      case HTTP_STATUS.BAD_REQUEST:
        // Extract validation errors if available
        if (data?.errors) {
          const errorMessages = Object.keys(data.errors).map((key) => {
            const messages = Array.isArray(data.errors[key])
              ? data.errors[key].join(", ")
              : data.errors[key];
            return `${key}: ${messages}`;
          });
          throw new Error(errorMessages.join("; "));
        }
        throw new Error(
          data?.title ||
            data?.message ||
            "Invalid request. Please check your input.",
        );

      case HTTP_STATUS.FORBIDDEN:
        throw new Error(
          data?.message || "You don't have permission to access this resource.",
        );

      case HTTP_STATUS.INTERNAL_SERVER_ERROR:
        throw new Error(
          data?.message || "Internal server error. Please try again later.",
        );

      case HTTP_STATUS.BAD_GATEWAY:
      case HTTP_STATUS.SERVICE_UNAVAILABLE:
        throw new Error(
          data?.message || "Service unavailable. Please try again later.",
        );

      default:
        throw new Error(
          data?.message || "Something went wrong. Please try again.",
        );
    }
  } else if (error.request) {
    // Network error
    throw new Error("Network error. Please check your internet connection.");
  } else {
    // Other error
    throw new Error(error.message || "An unexpected error occurred.");
  }
};

/**
 * Add authorization header if token exists
 */
const addAuthHeader = (config = {}) => {
  const token = getAuthToken();

  if (token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  return config;
};

/**
 * Axios wrapper with error handling and authorization
 */
export const apiClient = {
  /**
   * GET request
   * @param {string} url - Request URL
   * @param {object} config - Axios config object
   * @returns {Promise} Response data
   */
  async get(url, config = {}) {
    try {
      const configWithAuth = addAuthHeader(config);
      const response = await axios.get(url, configWithAuth);
      return response;
    } catch (error) {
      handleApiError(error);
    }
  },

  /**
   * POST request
   * @param {string} url - Request URL
   * @param {object} data - Request payload
   * @param {object} config - Axios config object
   * @returns {Promise} Response data
   */
  async post(url, data = {}, config = {}) {
    try {
      const configWithAuth = addAuthHeader(config);
      const response = await axios.post(url, data, configWithAuth);
      return response;
    } catch (error) {
      handleApiError(error);
    }
  },

  /**
   * PUT request
   * @param {string} url - Request URL
   * @param {object} data - Request payload
   * @param {object} config - Axios config object
   * @returns {Promise} Response data
   */
  async put(url, data = {}, config = {}) {
    try {
      const configWithAuth = addAuthHeader(config);
      const response = await axios.put(url, data, configWithAuth);
      return response;
    } catch (error) {
      handleApiError(error);
    }
  },

  /**
   * PATCH request
   * @param {string} url - Request URL
   * @param {object} data - Request payload
   * @param {object} config - Axios config object
   * @returns {Promise} Response data
   */
  async patch(url, data = {}, config = {}) {
    try {
      const configWithAuth = addAuthHeader(config);
      const response = await axios.patch(url, data, configWithAuth);
      return response;
    } catch (error) {
      handleApiError(error);
    }
  },

  /**
   * DELETE request
   * @param {string} url - Request URL
   * @param {object} config - Axios config object
   * @returns {Promise} Response data
   */
  async delete(url, config = {}) {
    try {
      const configWithAuth = addAuthHeader(config);
      const response = await axios.delete(url, configWithAuth);
      return response;
    } catch (error) {
      handleApiError(error);
    }
  },

  /**
   * Generic request method (for custom configurations)
   * @param {object} config - Full axios config object
   * @returns {Promise} Response data
   */
  async request(config) {
    try {
      const configWithAuth = addAuthHeader(config);
      const response = await axios.request(configWithAuth);
      return response;
    } catch (error) {
      handleApiError(error);
    }
  },

  /**
   * Login request - sends credentials and stores temp token
   * @param {string} url - Login endpoint URL
   * @param {object} credentials - { email, password }
   * @returns {Promise} Response with success flag and message
   */
  async login(url, credentials) {
    try {
      const response = await axios.post(url, credentials);

      if (response?.status === 200 && response.data) {
        // Store temp token for OTP verification if provided
        if (response.data?.token) {
          sessionStorage.setItem("tempAuthToken", response.data.token);
        }

        return {
          success: true,
          message: response.data?.message || "OTP sent to your email",
          data: response.data,
          token: response.data?.token,
        };
      }

      return {
        success: false,
        message: "Failed to send OTP. Please try again.",
        data: null,
      };
    } catch (error) {
      console.error("Login Error:", error);
      return {
        success: false,
        message:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Failed to login. Please check your credentials.",
        data: null,
      };
    }
  },

  /**
   * Verify OTP - validates OTP and stores auth data
   * @param {string} url - Verify OTP endpoint URL
   * @param {object} payload - { email, otp }
   * @returns {Promise} Response with success flag and user data
   */
  async verifyOTP(url, payload) {
    try {
      const token = sessionStorage.getItem("tempAuthToken");

      const config = {
        headers: { "Content-Type": "application/json" },
      };

      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await axios.post(url, payload, config);

      if (response?.status === 200 && response.data?.success) {
        // Clear temp token
        sessionStorage.removeItem("tempAuthToken");

        // Store access token
        if (response.data?.accessToken) {
          setAuthToken(response.data.accessToken);
        }

        // Store user data
        const userData = {
          email: response.data.email || payload.email,
          userName: response.data.userName,
          refreshToken: response.data.refreshToken,
        };
        setUserData(userData);

        return {
          success: true,
          message: response.data.message || "OTP verified successfully",
          data: response.data,
          token: response.data?.accessToken,
          user: userData,
        };
      }

      return {
        success: false,
        message: "Invalid OTP. Please check and try again.",
        data: null,
      };
    } catch (error) {
      console.error("Verify OTP Error:", error);
      console.error("Error Response:", error.response?.data);

      let errorMessage = "Invalid OTP";
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const errorMessages = Object.keys(errors).map(
          (key) => `${key}: ${errors[key].join(", ")}`,
        );
        errorMessage = errorMessages.join("; ");
      }

      return {
        success: false,
        message:
          error.response?.data?.title ||
          error.response?.data?.message ||
          errorMessage,
        error: error,
      };
    }
  },

  /**
   * Logout - clears all auth data and redirects to login
   */
  logout() {
    clearAuthData();
    window.location.href = "/login";
  },
};

export default apiClient;
